import { LOCALE_ID, NgModule } from '@angular/core';
import { AppComponent } from './app.component';
import {
    HttpClient,
    HttpClientModule,
    HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { CommonModule, DatePipe } from '@angular/common';

import { RecaptchaModule, RECAPTCHA_LANGUAGE } from 'ng-recaptcha';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

import localeFr from '@angular/common/locales/fr';
import { registerLocaleData } from '@angular/common';

import {
    MissingTranslationHandler,
    TranslateLoader,
    TranslateModule,
} from '@ngx-translate/core';
import { SideNavParentViewComponent } from './components/side-nav/side-nav-parent-view.component';

import { ClipboardModule } from '@angular/cdk/clipboard';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { DateAdapter, MAT_DATE_LOCALE, MatNativeDateModule, NativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxSpinnerModule } from 'ngx-spinner';
import { AppRoutingModule } from './app-routing.module';
import { RegisterComponent } from './components/register/register.component';
import { RegisterConfirmComponent } from './components/register-confirm/register-confirm.component';
import { SigninComponent } from './components/signin/signin.component';
import { StudentProfileViewComponent as StudentProfileViewComponent } from './components/student-profile/student-profile-view.component';
import { AuthInterceptorService } from './services/auth-interceptor.service';
import { ProfileComponent } from './components/profile/profile.component';
import { NoEmailFoundComponent } from './components/no-email-found/no-email-found.component';
import { HeaderComponent } from './components/shared/header/header.component';
import { PostFeedComponent } from './components/feed/post-feed/post-feed.component';
import { YouthWorkerProfileViewComponent } from './components/youth-worker-profile-view/youth-worker-profile-view.component';
import { TitleStrategy } from '@angular/router';
import { TemplatePageTitleStrategy } from './Strategies/template-page-title.strategy';
import { YouthWorkerCardComponent } from './components/youth-worker-profile-view/youth-worker-card/youth-worker-card.component';
import { StudentProfileCardComponent } from './components/student-profile/student-profile-card/student-profile-card.component';
import { StudentProfileCardAdditionalInfoComponent } from './components/student-profile/student-profile-card-additional-info/student-profile-card-additional-info.component';
import { ChatViewComponent } from './components/chat/chat-view/chat-view.component';
import { ChatBoxComponent } from './components/chat/chat-box/chat-box.component';
import { ChatMessageComponent } from './components/chat/chat-message/chat-message.component'

import { SafeHtmlPipe } from './pipe/SafeHtml.pipe';
import { FeedViewComponent } from './components/feed/feed-view/feed-view.component';
import { GoalsComponent } from './components/goals/goals-view/goals.component';
import { GoalsHeaderbarComponent } from './components/goals/goals-headerbar/goals-headerbar.component';
import { GoalsItemComponent } from './components/goals/goals-item/goals-item.component';
import { GoalsCreateModalComponent } from './components/goals/goals-modals/goals-create-modal/goals-create-modal.component';
import { GoalTaskComponent } from './components/goals/goals-modals/goal-task/goal-task.component';
import { GoalFormComponent } from './components/goals/goals-modals/goal-form/goal-form.component';

import { PathwaysUIModule } from './pathways-ui/pathways-ui.module';
import { MyMissingTranslationHandler } from './pathways-ui/services/missing-translation.service';
import { ErrorInterceptor } from './pathways-ui/_interceptors/error.interceptor';
import { LoadingInterceptor } from './pathways-ui/_interceptors/loading.interceptor';
import { CustomeTranslateHttpLoader } from './pathways-ui/services/translateHttpLoader';
import { GoalsUpdateModalComponent } from './components/goals/goals-modals/goals-update-modal/goals-update-modal.component';
import { GoalsDeleteModalComponent } from './components/goals/goals-modals/goals-delete-modal/goals-delete-modal.component';
import { AutocompleteStreetAddressComponent } from './pathways-ui/components/autocomplete-street-address-field/autocomplete-street-address-field.component';

registerLocaleData(localeFr);

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http);
}

export function CustomeHttpLoaderFactory(http: HttpClient) {
    return new CustomeTranslateHttpLoader(http);
}

export class CustomDateAdapter extends NativeDateAdapter {
    override format(date: Date, displayFormat: any): string {
		const day = date.getDate();
        const months = date.getMonth() + 1;
        const year = date.getFullYear();
		return year + '/' + months + '/' + day
    }
} 

@NgModule({
    declarations: [
        AppComponent,
        SideNavParentViewComponent,
        RegisterComponent,
        RegisterConfirmComponent,
        SigninComponent,
        StudentProfileViewComponent,

        ProfileComponent,
        NoEmailFoundComponent,
        HeaderComponent,
        PostFeedComponent,
        YouthWorkerProfileViewComponent,
        YouthWorkerCardComponent,
        StudentProfileCardComponent,
        StudentProfileCardAdditionalInfoComponent,
        ChatViewComponent,
        ChatBoxComponent,
        ChatMessageComponent,
        SafeHtmlPipe,
        FeedViewComponent,
        GoalsComponent,
        GoalsHeaderbarComponent,
        GoalsItemComponent,
        GoalsCreateModalComponent,
        GoalTaskComponent,
        GoalFormComponent,
        GoalsUpdateModalComponent,
        GoalsDeleteModalComponent,
        AutocompleteStreetAddressComponent
    ],
    imports: [
        CommonModule,
        MatDividerModule,
        BrowserModule,
        AppRoutingModule,
        FormsModule,
        HttpClientModule,
        FlexLayoutModule,
        BrowserAnimationsModule,
        MatDatepickerModule,
        MatIconModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatNativeDateModule,
        MatSidenavModule,
        MatExpansionModule,
        RecaptchaModule,
        MatTableModule,
        MatDialogModule,
        MatSnackBarModule,
        MatCardModule,
        MatChipsModule,
        MatPaginatorModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatToolbarModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        MatStepperModule,
        NgxSpinnerModule,
        ClipboardModule,
        MatRadioModule,
        MatTooltipModule,
        MatMenuModule,
        TranslateModule.forRoot({
            defaultLanguage: localStorage.getItem('language') || 'en',
            loader: {
                provide: TranslateLoader,
                useFactory: CustomeHttpLoaderFactory,
                deps: [HttpClient],
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: MyMissingTranslationHandler,
            },
            useDefaultLang: false,
        }),
        PathwaysUIModule
    ],
    providers: [
        {
            provide: RECAPTCHA_LANGUAGE,
            useFactory: (locale: string) => locale,
            deps: [LOCALE_ID],
        },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoadingInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptorService,
            multi: true,
        },
        DatePipe,
        {
            provide: TitleStrategy,
            useClass: TemplatePageTitleStrategy,

        },
        { provide: DateAdapter, useClass: CustomDateAdapter, deps: [MAT_DATE_LOCALE] },
    ],
    bootstrap: [AppComponent],
    entryComponents: [],
    exports: [SafeHtmlPipe]
})
export class AppModule {}
